import { useGetFormLookupsQuery } from "@/services";
import { node } from "prop-types";
import { createContext, useCallback, useContext, useMemo } from "react";
import { useIntl } from "react-intl";

export const FormPropertyValueLookupContext = createContext();
export const useFormPropertyValueLookupProvider = () =>
  useContext(FormPropertyValueLookupContext);

export const FormPropertyValueLookupProvider = ({ children }) => {
  //TODO: Might want to look into merging this into the FormPropertyProvider, to reduce an additional call to Cosmos
  const intl = useIntl();

  const { data: lookups, isFetching } = useGetFormLookupsQuery();

  const getPropertyValueDisplay = useCallback(
    (target, property, value) => {
      const lookupValue = lookups?.[target]?.[property]?.[value?.toLowerCase?.()];

      if (!lookupValue) return value;

      return intl.formatMessage({
        id: lookupValue.id,
        defaultMessage: lookupValue.fallback,
      });
    },
    [lookups, intl]
  );

  const getMultipleValuesDisplay = useCallback(
    (target, fieldName, values, formatNames) => {
      const labelArray = [];
      if (values != null) {
        if (Array.isArray(values)) {
          values.forEach((id) => {
            labelArray.push(getPropertyValueDisplay(target, fieldName, id));
          });
        } //exactly one item, just add the item.
        else {
          labelArray.push(getPropertyValueDisplay(target, fieldName, values));
        }
      }
      if (labelArray.length && formatNames) {
        for (let i = 0; i < labelArray.length; i++) {
          let name = labelArray[i];

          const nameAr = name.split(",");
          const firstName = nameAr[1].trim();
          const lastName = nameAr[0].trim();
          name = `${firstName} ${lastName}`;
          labelArray[i] = name;
        }
      }
      return labelArray.join(", ");
    },
    [getPropertyValueDisplay]
  );

  const getProperty = useCallback(
    (target, property) => lookups?.[target]?.[property],
    [lookups]
  );

  const getPropertyFallback = useCallback(
    (target, property, value) =>
      lookups?.[target]?.[property]?.[value]?.fallback || value,
    [lookups]
  );

  const getPropertyUserId = useCallback(
    (target, property, value) =>
      lookups?.[target]?.[property]?.[value]?.userId || null,
    [lookups]
  );

  const contextValue = useMemo(
    () => ({
      isLookupLoading: isFetching,
      getPropertyValueDisplay,
      getPropertyUserId,
      getPropertyFallback,
      getProperty,
      getMultipleValuesDisplay,
    }),
    [
      isFetching,
      getPropertyValueDisplay,
      getPropertyUserId,
      getPropertyFallback,
      getProperty,
      getMultipleValuesDisplay,
    ]
  );

  return (
    <FormPropertyValueLookupContext.Provider value={contextValue}>
      {children}
    </FormPropertyValueLookupContext.Provider>
  );
};

FormPropertyValueLookupProvider.propTypes = {
  children: node.isRequired,
};
